import type { CrossroadsLog } from '@/api/hooks/useIntegrationLogs'
import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons'
import {
  GraviButton,
  Horizontal,
  Texto,
  Vertical,
  useThemeContext,
} from '@gravitate-js/excalibrr'
import { Card, Tooltip, message } from 'antd'
import { BracesIcon } from 'lucide-react'
import SyntaxHighlighter from 'react-syntax-highlighter'
import {
  nnfxDark as dark,
  vs as light,
} from 'react-syntax-highlighter/dist/esm/styles/hljs'

const SYNTAX_HIGHLIGHTER_STYLE = { backgroundColor: 'transparent' }
const PRE_STYLE = {
  whiteSpace: 'pre-wrap' as const,
  margin: 0,
  fontSize: '12px',
}

async function copy(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text)
    message.success(`${label} copied`)
  } catch {
    // Rejects when the page isn't a secure context or permission is denied --
    // nothing to fall back to, so just say so rather than failing silently.
    message.error(`Could not copy ${label.toLowerCase()}`)
  }
}

function CopyButton({ text, label }: { text: string; label: string }) {
  return (
    <Tooltip title='Copy to clipboard'>
      <GraviButton
        size='small'
        appearance='text'
        icon={<CopyOutlined />}
        onClick={() => copy(text, label)}
      />
    </Tooltip>
  )
}

/** A debug box: titled, with the raw text behind a copy button. */
function DebugCard({
  title,
  icon,
  text,
  children,
}: React.PropsWithChildren<{
  title: string
  icon: React.ReactNode
  text: string
}>) {
  return (
    <Card
      size='small'
      title={
        <Horizontal gap={8} alignItems='center'>
          {icon}
          <span>{title}</span>
        </Horizontal>
      }
      extra={<CopyButton text={text} label={title} />}
    >
      {children}
    </Card>
  )
}

function JsonBlock({ json }: { json: string }) {
  const { isDarkTheme } = useThemeContext()
  return (
    // @ts-expect-error SyntaxHighlighter types don't match runtime props
    <SyntaxHighlighter
      language='json'
      style={isDarkTheme ? dark : light}
      customStyle={SYNTAX_HIGHLIGHTER_STYLE}
    >
      {json}
    </SyntaxHighlighter>
  )
}

export function DebugTabContent({ log }: { log: CrossroadsLog }) {
  // Legacy `crossroads` details and V3 `system_v3` details both carry this.
  const traceback = 'traceback' in log.detail ? log.detail.traceback : null
  // What the destination system itself said, when the headline message is our own
  // friendlier summary of it.
  const rawResponse =
    'raw_response' in log.detail ? log.detail.raw_response : null
  const outbound = log.outbound_request
    ? JSON.stringify(log.outbound_request, null, 2)
    : null
  const inbound = log.inbound_request
    ? JSON.stringify(log.inbound_request, null, 2)
    : null
  const hasAnything =
    !!log.correlation_id ||
    !!traceback ||
    !!rawResponse ||
    !!outbound ||
    !!inbound

  return (
    <Vertical gap={16} className='mt-4'>
      {log.correlation_id && `Correlation ID: ${log.correlation_id}`}
      {traceback && (
        <DebugCard
          title='Stack Trace'
          icon={<BracesIcon size={16} />}
          text={traceback}
        >
          <pre style={PRE_STYLE}>{traceback}</pre>
        </DebugCard>
      )}
      {rawResponse && (
        <DebugCard
          title='Destination Response'
          icon={<BracesIcon size={16} />}
          text={rawResponse}
        >
          <pre style={PRE_STYLE}>{rawResponse}</pre>
        </DebugCard>
      )}
      {outbound && (
        <DebugCard
          title='Outbound Request'
          icon={<InfoCircleOutlined />}
          text={outbound}
        >
          <JsonBlock json={outbound} />
        </DebugCard>
      )}
      {inbound && (
        <DebugCard
          title='Inbound Request'
          icon={<InfoCircleOutlined />}
          text={inbound}
        >
          <JsonBlock json={inbound} />
        </DebugCard>
      )}
      {!hasAnything && (
        <Texto appearance='hint'>No debug information captured</Texto>
      )}
    </Vertical>
  )
}
